<template>
  <div class="py-2 px-4">
    <div class="overflow-hidden border-b border-gray-200 rounded-lg shadow">
      <div class="px-6 py-2 text-gray-500 bg-gray-50 border-b border-gray-200">
        Search for Discount codes
      </div>
      <div class="px-6 py-4 space-y-4 text-gray-500 bg-white">
        <div class="flex flex-row space-x-4">
          <div>
            <icon-fgt :name="selectedLogo" class="w-32 h-32" />
          </div>
          <div class="w-full">
            <form @submit.prevent="searchDiscount" class="space-y-4">
              <div>
                <label :for="`cart-select-`" class="block text-sm font-medium leading-5 text-gray-700">Select Cart</label>
                <select
                  :id="`cart-select-`"
                  v-model="cartId"
                  class="mt-1 block form-select w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:shadow-outline-blue focus:border-blue-300 transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                >
                  <option
                    v-for="cart in carts"
                    :key="cart.ID"
                    :value="cart.ID"
                  >
                    {{ cart.CartName }}
                  </option>
                </select>
              </div>
              <div>
                <label :for="`discount-code-${_uid}`" class="block text-sm font-medium leading-5 text-gray-700 mt-2">Discount Code</label>
                <div class="relative rounded-md shadow-sm">
                  <input
                    :id="`discount-code-${_uid}`"
                    v-model="discountCode"
                    class="form-input block w-full sm:text-sm sm:leading-5"
                    placeholder="Search..."
                  />
                </div>
              </div>
              <div class="flex justify-end">
                  <Button
                    type="submit"
                    :disabled="! discountCode || processing"
                    class="flex items-center space-x-2"
                  >
                    <span>Search</span>
                    <LoadingSpinner v-if="processing" />
                  </Button>
              </div>
            </form>
          </div>
        </div>

        <HttpError :error="error" dismissable />

        <div v-if="results.length" class="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
          <table class="w-full">
            <thead class="bg-gray-50 border-b border-gray-200">
              <tr>
                <th class="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Cart
                </th>
                <th class="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Discount Code
                </th>
                <th class="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th class="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Usage
                </th>
              </tr>
            </thead>
            <tr
              v-for="(result, index) in results"
              :key="index"
              :class="{
                'bg-white': ! (index % 2),
                'bg-gray-50': index % 2
              }"
            >
              <td class="px-6 py-4 whitespace-nowrap text-center tracking-wider">
                {{ result.cart }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-center tracking-wider font-medium text-gray-900">
                {{ result.code }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-center">
                <DiscountStatus :status="result.status" />
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-center tracking-wider font-medium text-gray-900">
                <span v-html="result.usage"></span>
              </td>
            </tr>
          </table>
        </div>

      </div>
    </div>
  </div>

</template>
<script>
import allCarts from "@/data/carts.json";
import { ApiFactory } from "@/api/ApiFactory.js";
import DiscountStatus from "./DiscountStatus.vue";
import Button from "@/components/elements/Button.vue";
import discountStatuses from "@/data/discountStatuses.js";

const Discounts = ApiFactory.get("discounts");

export default {
  name: "SearchDiscounts",
  components: {
    Button,
    DiscountStatus
  },
  data() {
    return{
      cartId: null,
      discountCode: null,
      results: [],
      error: null,
      processing: false
    }
  },
  computed: {
    carts() {
      return allCarts.filter((cart) => [22, 23, 31, 32].includes(cart.ID));
    },
    selectedCart() {
      return this.carts.find((cart) => cart.ID === this.cartId);
    },
    selectedLogo() {
      return this.selectedCart?.LogoName;
    }
  },
  created() {
    this.cartId = this.carts[0].ID;
  },
  methods: {
    searchDiscount() {
      this.error = null;
      this.processing = true;

      Discounts.search({
          cartId: this.selectedCart?.ID,
          code: this.discountCode,
        })
        .then((response) => {
          this.results.unshift(
            this.getDiscountResult(response.data.body?.discount_code, response.data.body?.price_rule)
          );
          this.resetForm();
        })
        .catch((error) => this.error = error)
        .finally(() => this.processing = false);
    },
    resetForm() {
      this.discountCode = null;
    },
    getDiscountResult(discount_code, price_rule) {
      if (discount_code?.code === undefined) {
        return this.getNotFoundDiscountResult();
      }

      return this.getFoundDiscountResult(discount_code, price_rule);
    },
    getFoundDiscountResult(discount_code, price_rule) {
      return {
        cart: allCarts.find((cart) => cart.ID === this.selectedCart?.ID)?.CartName,
        code: discount_code?.code,
        usage: discount_code?.usage_count + ' / ' + (price_rule?.usage_limit || '&infin;'),
        status: this.getDiscountStatus(price_rule)
      };
    },
    getNotFoundDiscountResult() {
      return {
        cart: allCarts.find((cart) => cart.ID === this.selectedCart?.ID)?.CartName,
        code: this.discountCode,
        usage: '',
        status: 'Not Found'
      };
    },
    getDiscountStatus(price_rule) {
      let now = (new Date()).getTime();
      let start = (new Date(price_rule?.starts_at)).getTime();
      let end = now + 1; // assuming no end date

      if (price_rule?.ends_at) {
        end = (new Date(price_rule?.ends_at)).getTime();
      }

      if (now < start) {
        return discountStatuses.Scheduled;
      }

      if (now > end) {
        return discountStatuses.Expired;
      }

      return discountStatuses.Active;
    }
  }
};
</script>
